/**
 * API Service para Imágenes de Citas (ANTES del mantenimiento)
 * 
 * Maneja todas las operaciones CRUD para imágenes asociadas a citas,
 * siguiendo la arquitectura estándar del proyecto con normalizedApiClient.
 */

import { apiHelpers } from './normalizedApiClient';
import { API_ENDPOINTS } from '../config/api';
import apiClient from './apiClient';
import ImageCompressionService from './imageCompressionService';
import { toast } from 'react-hot-toast';

/**
 * Obtiene todas las imágenes de una cita
 * @param {string|number} citaId - ID de la cita
 * @returns {Promise<Array>} - Lista de imágenes
 */
export const obtenerImagenesCita = async (citaId) => {
  try {
    console.log('🖼️ [CitaImagenesService] Obteniendo imágenes para cita:', citaId);
    
    if (!citaId) {
      return [];
    }
    
    const response = await apiHelpers.generic.get(API_ENDPOINTS.CITAS.IMAGENES(citaId));
    
    if (response.data) {
      const imagenesData = response.data.imagenes || response.data;
      console.log(`✅ [CitaImagenesService] ${imagenesData.length} imágenes encontradas`);
      
      return imagenesData.map(imagen => ({
        id: imagen.id,
        nombre_archivo: imagen.nombre_archivo,
        url_storage: imagen.url_storage,
        thumbnail_url: imagen.thumbnail_url,
        descripcion: imagen.descripcion,
        orden: imagen.orden,
        tamaño_kb: imagen.tamaño_kb,
        dimensiones: {
          ancho: imagen.ancho_px,
          alto: imagen.alto_px
        },
        created_at: imagen.created_at,
        created_by: imagen.created_by
      }));
    }
    
    return [];
  } catch (error) {
    console.error(`❌ [CitaImagenesService] Error al obtener imágenes de cita ${citaId}:`, error);
    throw error;
  }
};

/**
 * Sube imagen completa (compresión + API backend)
 * @param {string|number} citaId - ID de la cita
 * @param {File} archivo - Archivo de imagen
 * @param {string} descripcion - Descripción opcional
 * @param {number} orden - Orden de la imagen
 * @param {string} nombreArchivo - Nombre personalizado para el archivo
 * @returns {Promise<Object>} - Imagen subida
 */
export const subirImagen = async (citaId, archivo, descripcion = '', orden = 0, nombreArchivo = null) => {
  try {
    console.log('🖼️ [CitaImagenesService] Iniciando subida de imagen para cita:', citaId);

    if (!citaId || !archivo) {
      throw new Error('ID de cita y archivo son requeridos');
    }

    // 1. Comprimir imagen
    const compresionResult = await ImageCompressionService.compressImage(archivo);
    
    // 2. Preparar FormData para envío a API
    const formData = new FormData();
    formData.append('imagen', compresionResult.compressedFile);
    formData.append('descripcion', descripcion || '');
    formData.append('orden', orden.toString());
    formData.append('nombreArchivo', nombreArchivo || archivo.name);

    // 3. Para FormData necesitamos usar apiClient directamente
    // Los interceptors de autenticación se aplicarán automáticamente
    const response = await apiClient.post(API_ENDPOINTS.CITAS.IMAGENES(citaId), formData, {
      headers: {
        // NO especificar Content-Type para FormData - el browser lo hace automáticamente
      }
    });

    if (response.data) {
      const imagenCreada = response.data.imagen || response.data;
      console.log('✅ [CitaImagenesService] Imagen subida exitosamente:', imagenCreada.id);
      
      return {
        id: imagenCreada.id,
        nombre_archivo: imagenCreada.nombre_archivo,
        url_storage: imagenCreada.url_storage,
        thumbnail_url: imagenCreada.thumbnail_url,
        descripcion: imagenCreada.descripcion,
        orden: imagenCreada.orden,
        tamaño_kb: imagenCreada.tamaño_kb,
        dimensiones: {
          ancho: imagenCreada.ancho_px,
          alto: imagenCreada.alto_px
        },
        created_at: imagenCreada.created_at,
        metadata: {
          originalSize: compresionResult.originalSize,
          compressedSize: compresionResult.compressedSize,
          compressionRatio: compresionResult.compressionRatio
        }
      };
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error('❌ [CitaImagenesService] Error en subirImagen:', error);
    toast.error(`Error subiendo imagen: ${error.message}`);
    throw error;
  }
};

/**
 * Elimina una imagen específica
 * @param {string|number} citaId - ID de la cita
 * @param {string|number} imagenId - ID de la imagen
 * @returns {Promise<boolean>} - Éxito de la eliminación
 */
export const eliminarImagen = async (citaId, imagenId) => {
  try {
    console.log(`🗑️ [CitaImagenesService] Eliminando imagen ${imagenId} de cita ${citaId}`);
    
    if (!citaId || !imagenId) {
      throw new Error('ID de cita e imagen son requeridos');
    }
    
    const response = await apiHelpers.generic.delete(API_ENDPOINTS.CITAS.IMAGEN_BY_ID(citaId, imagenId));
    
    if (response.data) {
      console.log('✅ [CitaImagenesService] Imagen eliminada exitosamente:', imagenId);
      return true;
    }
    
    throw new Error('No se recibió respuesta válida del servidor');
  } catch (error) {
    console.error(`❌ [CitaImagenesService] Error al eliminar imagen ${imagenId}:`, error);
    toast.error(`Error eliminando imagen: ${error.message}`);
    throw error;
  }
};

/**
 * Obtiene estadísticas de imágenes para una cita
 * @param {string|number} citaId - ID de la cita
 * @returns {Promise<Object>} - Estadísticas de imágenes
 */
export const obtenerEstadisticasCita = async (citaId) => {
  try {
    console.log('📊 [CitaImagenesService] Obteniendo estadísticas de imágenes para cita:', citaId);
    
    // Obtener las imágenes primero
    const imagenes = await obtenerImagenesCita(citaId);
    
    const totalImagenes = imagenes.length;
    const tamañoTotal = imagenes.reduce((sum, img) => sum + (img.tamaño_kb || 0), 0);
    
    return {
      total_imagenes: totalImagenes,
      tamaño_total_kb: tamañoTotal,
      tamaño_total_mb: (tamañoTotal / 1024).toFixed(2),
      primera_imagen: totalImagenes > 0 ? imagenes[0].created_at : null,
      ultima_imagen: totalImagenes > 0 ? imagenes[imagenes.length - 1].created_at : null
    };
  } catch (error) {
    console.error(`❌ [CitaImagenesService] Error al obtener estadísticas de cita ${citaId}:`, error);
    throw error;
  }
};

/**
 * Hook personalizado para usar el servicio de imágenes de citas
 */
export const useCitaImagenesApiService = () => {
  return {
    obtenerImagenesCita,
    subirImagen,
    eliminarImagen,
    obtenerEstadisticasCita
  };
};

// Mantener compatibilidad con implementación anterior (clase estática)
class CitaImagenesApiService {
  static async obtenerImagenesCita(_session, citaId) {
    return obtenerImagenesCita(citaId);
  }
  
  static async subirImagen(_session, citaId, _empresaId, _sucursalId, archivo, descripcion = '', orden = 0, nombreArchivo = null) {
    return subirImagen(citaId, archivo, descripcion, orden, nombreArchivo);
  }
  
  static async eliminarImagen(_session, citaId, imagenId) {
    return eliminarImagen(citaId, imagenId);
  }
  
  static async obtenerEstadisticasCita(_session, citaId) {
    return obtenerEstadisticasCita(citaId);
  }
}

export default CitaImagenesApiService;